import type { DayHours, Pool, SeasonWindow, SplashPad } from "./types";
import { SPLASH_PADS, SPLASH_SEASON } from "./splash-pads";

// Real DC DPR 2026 pool catalog. Pool identities (name/address/ward), per-pool
// hours, and the seasonal structure were compiled from dpr.dc.gov pages (see
// docs/dpr-refresh.md for the refresh process). Encoded here as typed
// SEED_POOLS so the dev loop and `scripts/seed.ts` upsert the same data that
// scripts/seed.ts pushes to real AWS.
//
// Seasonal rules (2026):
//  - Outdoor pools run weekends-only (Sat/Sun 10:00-18:00) May 23 – Jun 21,
//    plus Memorial Day (Mon May 25, 10:00-18:00), then a full per-pool schedule
//    Jun 22 – Sep 7. Each pool runs SIX days a week with its own rotating
//    weekday closure and its own weekday open/close; weekends stay 10:00-18:00.
//  - Children's/kiddie sections are closed Mondays AND Tuesdays citywide.
//  - Indoor aquatic centers run a year-round schedule (no Mon/Tue kiddie rule).
//
// Active-pool coordinates are web-verified (OSM Nominatim/Overpass, cross-checked
// against DC bounds and reverse geocode); renovation-pool coordinates remain
// address-estimated.
// hasKiddiePool confidence varies per pool — see inline notes; `false` means
// "no evidence found", not "definitively absent".

// ---- Shared time constants ----------------------------------------------

const WEEKEND_HOURS: DayHours = { open: "10:00", close: "18:00" };
const MEMORIAL_DAY = "2026-05-25";
const WEEKENDS_ONLY = { start: "2026-05-23", end: "2026-06-21" };
const FULL_SEASON = { start: "2026-06-22", end: "2026-09-07" };
const INDOOR_YEAR = { start: "2026-01-01", end: "2026-12-31" };

/** Sun=0 … Sat=6. */
const SUN = 0;
const MON = 1;
const TUE = 2;
const SAT = 6;

type Weekday = "Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat";
const WEEKDAY_INDEX: Record<Weekday, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

// ---- Schedule builders ----------------------------------------------------

/** All 7 days closed; callers fill in the open days. */
function allClosed(): Array<DayHours | null> {
  return Array.from({ length: 7 }, () => null as DayHours | null);
}

/** Memorial-Day single-day window: main open 10:00-18:00 every day. */
function memorialDayWindow(): SeasonWindow {
  return {
    start: MEMORIAL_DAY,
    end: MEMORIAL_DAY,
    main: { days: Array.from({ length: 7 }, () => ({ ...WEEKEND_HOURS })) },
    // Kiddie omitted on the holiday window.
  };
}

/** Weekends-only window: Sat/Sun 10:00-18:00, all other days closed. */
function weekendsOnlyWindow(hasKiddie: boolean): SeasonWindow {
  const days = allClosed();
  days[SAT] = { ...WEEKEND_HOURS };
  days[SUN] = { ...WEEKEND_HOURS };
  const w: SeasonWindow = { ...WEEKENDS_ONLY, main: { days: [...days] } };
  if (hasKiddie) {
    // Weekday Mon/Tue are already closed here, so the citywide kiddie rule is
    // moot during the weekends-only window; the kiddie schedule mirrors main.
    w.kiddie = { days: [...days] };
  }
  return w;
}

/**
 * Full-season window. Weekday hours come from the pool's `fullSeasonMain`; the
 * pool's rotating closure day(s) are null; weekends are always 10:00-18:00.
 * Kiddie mirrors main but is additionally closed Mon & Tue (citywide rule).
 */
function fullSeasonWindow(
  weekday: DayHours,
  closedWeekdays: number[],
  hasKiddie: boolean,
): SeasonWindow {
  // Main: weekdays = weekday hours, weekends = 10:00-18:00, closures = null.
  const mainDays: Array<DayHours | null> = Array.from({ length: 7 }, () => ({ ...weekday }));
  mainDays[SAT] = { ...WEEKEND_HOURS };
  mainDays[SUN] = { ...WEEKEND_HOURS };
  for (const d of closedWeekdays) mainDays[d] = null;

  const w: SeasonWindow = { ...FULL_SEASON, main: { days: mainDays } };

  if (hasKiddie) {
    const kiddieDays = [...mainDays];
    kiddieDays[MON] = null; // citywide children's-pool closure
    kiddieDays[TUE] = null;
    w.kiddie = { days: kiddieDays };
  }
  return w;
}

/** Indoor year-round window: weekday hours Mon-Fri, weekend hours Sat/Sun. */
function indoorWindow(weekday: DayHours, weekend: DayHours, hasKiddie: boolean): SeasonWindow {
  const days: Array<DayHours | null> = [
    { ...weekend }, // Sun
    { ...weekday }, // Mon
    { ...weekday }, // Tue
    { ...weekday }, // Wed
    { ...weekday }, // Thu
    { ...weekday }, // Fri
    { ...weekend }, // Sat
  ];
  const w: SeasonWindow = { ...INDOOR_YEAR, main: { days } };
  // No Mon/Tue kiddie closure indoors; kiddie mirrors main when present.
  if (hasKiddie) w.kiddie = { days: [...days] };
  return w;
}

// ---- Pool builders --------------------------------------------------------

interface OutdoorSpec {
  poolId: string;
  name: string;
  address: string;
  ward: number;
  lat: number;
  lng: number;
  hasKiddiePool: boolean;
  /** Full-season weekday open/close. */
  weekday: DayHours;
  /** Single rotating closure day, or array (standalone children's pools). */
  closedWeekday: Weekday | Weekday[];
  /** Standalone children's pool (the whole basin IS the kiddie section). */
  standaloneChildrensPool?: boolean;
  /** Pool has a water slide (adds a crowd-reportable slide section). */
  hasWaterslide?: boolean;
  /** Curated: gradual zero-depth (sloped) entry + shallow wading area for small children (drives a neutral info badge). */
  slopedEntry?: boolean;
}

function outdoorPool(spec: OutdoorSpec): Pool {
  const closures = (Array.isArray(spec.closedWeekday) ? spec.closedWeekday : [spec.closedWeekday]).map(
    (d) => WEEKDAY_INDEX[d],
  );
  return {
    poolId: spec.poolId,
    name: spec.name,
    address: spec.address,
    ward: spec.ward,
    lat: spec.lat,
    lng: spec.lng,
    hasKiddiePool: spec.hasKiddiePool,
    ...(spec.hasWaterslide ? { hasWaterslide: true } : {}),
    ...(spec.slopedEntry ? { slopedEntry: true } : {}),
    // A standalone children's pool's entire basin IS the kiddie section, so it
    // renders no separate Main tile (see `childrenOnly` in shared/types.ts).
    ...(spec.standaloneChildrensPool ? { childrenOnly: true } : {}),
    indoor: false,
    // Order matters: the evaluator is first-match, so the single-day Memorial
    // Day window must precede the broader weekends-only window.
    seasons: [
      memorialDayWindow(),
      weekendsOnlyWindow(spec.hasKiddiePool),
      fullSeasonWindow(spec.weekday, closures, spec.hasKiddiePool),
    ],
  };
}

interface IndoorSpec {
  poolId: string;
  name: string;
  address: string;
  ward: number;
  lat: number;
  lng: number;
  hasKiddiePool: boolean;
  weekday: DayHours;
  weekend: DayHours;
  /** Pool has a water slide (adds a crowd-reportable slide section). */
  hasWaterslide?: boolean;
}

function indoorPool(spec: IndoorSpec): Pool {
  return {
    poolId: spec.poolId,
    name: spec.name,
    address: spec.address,
    ward: spec.ward,
    lat: spec.lat,
    lng: spec.lng,
    hasKiddiePool: spec.hasKiddiePool,
    ...(spec.hasWaterslide ? { hasWaterslide: true } : {}),
    indoor: true,
    seasons: [indoorWindow(spec.weekday, spec.weekend, spec.hasKiddiePool)],
  };
}

// Standard indoor hours (DPR aquatic centers): MEDIUM confidence — shift by
// program / DCPS sharing.
const INDOOR_WEEKDAY: DayHours = { open: "06:00", close: "21:00" };
const INDOOR_WEEKEND: DayHours = { open: "10:00", close: "17:00" };

// ---- Splash-pad builder ---------------------------------------------------
//
// Splash pads (DPR spray parks) are crowd-reportable Pools with `kind: "splash"`
// and no kiddie section (reported on `main`). Their REAL 2026 season:
//  - Weekends only (Sat/Sun 10:00-18:00) May 23 – Jun 21.
//  - Daily 10:00-18:00 Jun 22 – Sep 7.
// The per-pad weekly maintenance window is a mid-day gap a single-open/close
// DayHours can't represent, so it is NOT encoded here — it stays informational
// on the Splash tab card (read from SPLASH_PADS).

// Both windows come from SPLASH_SEASON (shared/splash-pads.ts), which is also
// what the Splash tab's date-aware schedule line reads — one copy of the 2026
// season calendar, not two that can drift apart.
const SPLASH_WEEKENDS_ONLY = SPLASH_SEASON.weekendsOnly ?? { start: "2026-05-23", end: "2026-06-21" };
const SPLASH_DAILY = SPLASH_SEASON.daily ?? { start: "2026-06-22", end: "2026-09-07" };

/** Splash weekends-only window: Sat/Sun 10:00-18:00, weekdays closed. */
function splashWeekendsWindow(): SeasonWindow {
  const days = allClosed();
  days[SAT] = { ...WEEKEND_HOURS };
  days[SUN] = { ...WEEKEND_HOURS };
  return { ...SPLASH_WEEKENDS_ONLY, main: { days } };
}

/** Splash daily window: open 10:00-18:00 every day. */
function splashDailyWindow(): SeasonWindow {
  const days = Array.from({ length: 7 }, () => ({ ...WEEKEND_HOURS }));
  return { ...SPLASH_DAILY, main: { days } };
}

function splashPool(pad: SplashPad): Pool {
  return {
    poolId: `splash-${pad.id}`,
    name: pad.name,
    address: pad.address,
    ward: pad.ward,
    lat: pad.lat,
    lng: pad.lng,
    kind: "splash",
    hasKiddiePool: false,
    indoor: false,
    seasons: [splashWeekendsWindow(), splashDailyWindow()],
  };
}

/** The 35 DPR spray parks as crowd-reportable splash Pools. */
export const SPLASH_POOLS: Pool[] = SPLASH_PADS.map(splashPool);

// ---- Catalog --------------------------------------------------------------
//
// TODO: standalone children's pools (Happy Hollow, Park View, Lincoln Capper)
// close 14:00-15:00 daily for cleaning; the single-window DayHours schema
// cannot express an intra-day gap, so that break is NOT modeled. They read as
// continuously open 10:00-18:00 on open days.

export const SEED_POOLS: Pool[] = [
  // ----- Outdoor: full-feature pools -----
  outdoorPool({
    poolId: "banneker",
    name: "Banneker Pool",
    address: "2500 Georgia Avenue NW, Washington, DC 20001",
    ward: 1,
    lat: 38.92270,
    lng: -77.02259,
    hasKiddiePool: true, // MEDIUM: listed as a children's pool in DPR accessibility list, no itemized basin
    weekday: { open: "10:00", close: "20:00" },
    closedWeekday: "Thu",
  }),
  // Standalone children's pool: the whole basin is the kiddie section, so it is
  // closed Mon & Tue (encoded as the closure day) — see TODO re: cleaning break.
  outdoorPool({
    poolId: "happy-hollow",
    name: "Happy Hollow Children's Pool",
    address: "2200 Champlain Street NW, Washington, DC 20009",
    ward: 1,
    lat: 38.91912,
    lng: -77.04098,
    hasKiddiePool: true, // HIGH: standalone children's pool
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: ["Mon", "Tue"],
    standaloneChildrensPool: true,
  }),
  outdoorPool({
    poolId: "park-view",
    name: "Park View Children's Pool",
    address: "693 Otis Place NW, Washington, DC 20010",
    ward: 1,
    lat: 38.93502,
    lng: -77.02138,
    hasKiddiePool: true, // HIGH: standalone children's pool
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: ["Mon", "Tue"],
    standaloneChildrensPool: true,
  }),
  outdoorPool({
    poolId: "francis",
    name: "Francis Pool",
    address: "2435 N Street NW, Washington, DC 20037",
    ward: 2,
    lat: 38.90744,
    lng: -77.05354,
    hasKiddiePool: false, // no evidence of a kiddie section
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: "Tue",
  }),
  outdoorPool({
    poolId: "volta-park",
    name: "Volta Park Pool",
    address: "1555 34th Street NW, Washington, DC 20007",
    ward: 2,
    lat: 38.91036,
    lng: -77.06714,
    hasKiddiePool: false,
    weekday: { open: "12:00", close: "20:00" },
    closedWeekday: "Mon",
  }),
  outdoorPool({
    poolId: "hearst",
    name: "Hearst Pool",
    address: "3701 37th Street NW, Washington, DC 20016",
    ward: 3,
    lat: 38.93951,
    lng: -77.07166,
    hasKiddiePool: false,
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    weekday: { open: "12:00", close: "20:00" },
    closedWeekday: "Thu",
  }),
  outdoorPool({
    poolId: "upshur",
    name: "Upshur Pool",
    address: "4300 Arkansas Avenue NW, Washington, DC 20011",
    ward: 4,
    lat: 38.94330,
    lng: -77.03229,
    hasKiddiePool: true, // HIGH: DPR page explicitly lists a "Kiddie Wading Pool"
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: "Mon",
  }),
  outdoorPool({
    poolId: "harry-thomas",
    name: "Harry Thomas Sr. Pool",
    address: "1743 Lincoln Road NE, Washington, DC 20002",
    ward: 5,
    lat: 38.91445,
    lng: -77.00721,
    hasKiddiePool: true, // MEDIUM: named "children's pool" in DPR list, no itemized basin
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: "Tue",
  }),
  outdoorPool({
    poolId: "langdon-park",
    name: "Langdon Park Pool",
    address: "2860 Mills Avenue NE, Washington, DC 20018",
    ward: 5,
    lat: 38.92684,
    lng: -76.97581,
    hasKiddiePool: false,
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: "Thu",
  }),
  outdoorPool({
    poolId: "theodore-hagans",
    name: "Theodore Hagans Pool",
    address: "3201 Fort Lincoln Drive NE, Washington, DC 20018",
    ward: 5,
    lat: 38.92505,
    lng: -76.95851,
    hasKiddiePool: false,
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: "Mon",
  }),
  outdoorPool({
    poolId: "kenilworth",
    name: "Kenilworth Pool",
    address: "4321 Ord Street NE, Washington, DC 20019",
    ward: 7,
    lat: 38.90846,
    lng: -76.94174,
    hasKiddiePool: false,
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: "Wed",
  }),
  outdoorPool({
    poolId: "ridge-road",
    name: "Ridge Road Pool",
    address: "830 Ridge Road SE, Washington, DC 20019",
    ward: 7,
    lat: 38.87595,
    lng: -76.94049,
    hasKiddiePool: false,
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    weekday: { open: "12:00", close: "20:00" },
    closedWeekday: "Thu",
  }),
  outdoorPool({
    poolId: "woody-ward",
    name: "Woody Ward Pool",
    address: "5100 Southern Avenue SE, Washington, DC 20019",
    ward: 7,
    lat: 38.87980,
    lng: -76.92690,
    hasKiddiePool: true, // MEDIUM: named "children's pool" in DPR list, no itemized basin
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: "Thu",
  }),
  outdoorPool({
    poolId: "anacostia",
    name: "Anacostia Pool",
    address: "1800 Anacostia Drive SE, Washington, DC 20020",
    ward: 8,
    lat: 38.87238,
    lng: -76.98294,
    hasKiddiePool: false,
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: "Mon",
  }),
  outdoorPool({
    poolId: "fort-stanton",
    name: "Fort Stanton Pool",
    address: "1800 Erie Street SE, Washington, DC 20020",
    ward: 8,
    lat: 38.85770,
    lng: -76.97798,
    hasKiddiePool: false,
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: "Thu",
  }),
  // ward 6 per actual address (555 L St SE); DPR groups it under Ward 8.
  outdoorPool({
    poolId: "lincoln-capper",
    name: "Lincoln Capper Children's Pool",
    address: "555 L Street SE, Washington, DC 20003",
    ward: 6,
    lat: 38.87730,
    lng: -76.99839,
    hasKiddiePool: true, // standalone children's pool
    weekday: { open: "10:00", close: "18:00" },
    closedWeekday: ["Mon", "Tue"],
    standaloneChildrensPool: true,
  }),
  outdoorPool({
    poolId: "oxon-run",
    name: "Oxon Run Pool",
    address: "501 Mississippi Avenue SE, Washington, DC 20032",
    ward: 8,
    lat: 38.83674,
    lng: -76.99860,
    hasKiddiePool: false,
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    weekday: { open: "12:00", close: "20:00" },
    closedWeekday: "Mon",
  }),

  // ----- Indoor aquatic centers (year-round; MEDIUM-confidence hours) -----
  indoorPool({
    poolId: "marie-reed-indoor",
    name: "Marie Reed Aquatic Center",
    address: "2200 Champlain Street NW, Washington, DC 20009",
    ward: 1,
    lat: 38.91888,
    lng: -77.04123,
    hasKiddiePool: false,
    weekday: INDOOR_WEEKDAY,
    weekend: INDOOR_WEEKEND,
  }),
  indoorPool({
    poolId: "wilson-indoor",
    name: "Wilson Aquatic Center",
    address: "4551 Fort Drive NW, Washington, DC 20016",
    ward: 3,
    lat: 38.94882,
    lng: -77.07768,
    hasKiddiePool: true, // MEDIUM
    weekday: INDOOR_WEEKDAY,
    weekend: INDOOR_WEEKEND,
  }),
  indoorPool({
    poolId: "takoma-indoor",
    name: "Takoma Aquatic Center",
    address: "300 Van Buren Street NW, Washington, DC 20012",
    ward: 4,
    lat: 38.96890,
    lng: -77.01810,
    hasKiddiePool: true, // MEDIUM
    weekday: INDOOR_WEEKDAY,
    weekend: INDOOR_WEEKEND,
  }),
  indoorPool({
    poolId: "turkey-thicket-indoor",
    name: "Turkey Thicket Aquatic Center",
    address: "1100 Michigan Avenue NE, Washington, DC 20017",
    ward: 5,
    lat: 38.93809,
    lng: -76.99282,
    hasKiddiePool: true, // MEDIUM
    weekday: INDOOR_WEEKDAY,
    weekend: INDOOR_WEEKEND,
  }),
  indoorPool({
    poolId: "rumsey-indoor",
    name: "William H. Rumsey Aquatic Center",
    address: "635 North Carolina Avenue SE, Washington, DC 20003",
    ward: 6,
    lat: 38.88633,
    lng: -76.99701,
    hasKiddiePool: false,
    weekday: INDOOR_WEEKDAY,
    weekend: INDOOR_WEEKEND,
  }),
  indoorPool({
    poolId: "deanwood-indoor",
    name: "Deanwood Aquatic Center",
    address: "1350 49th Street NE, Washington, DC 20019",
    ward: 7,
    lat: 38.90839,
    lng: -76.93245,
    hasKiddiePool: true, // MEDIUM
    hasWaterslide: true, // HIGH: DPR page — "boasts an indoor slide" (restricted hours)
    weekday: INDOOR_WEEKDAY,
    weekend: INDOOR_WEEKEND,
  }),
  // ---- Closed for the 2026 season (shown in the app, but not reportable) ----
  // Renovation closures that are out all summer. Unlike CLOSED_FOR_RENOVATION_2026
  // below (which is fully HIDDEN), these carry `closedForSeason: true` so they DO
  // appear as a "Closed for the season" card — telling families not to make the
  // trip. Empty `seasons` makes them read out-of-season everywhere; the flag also
  // overrides the kiddie-only visibility filter in src/store.ts.
  //
  // Barry Farm Aquatic Center — indoor, has a water slide, but currently OFFLINE
  // per a DPR alert (verified 2026-06-12, (202) 442-5328). Normal hours when
  // open: M-F 6am-9pm, Sat-Sun 10am-5pm (model like the other *-indoor aquatic
  // centers). Flagged `closedForSeason` so it does NOT read as open — it renders
  // the non-reportable "Closed" card and stays out-of-season everywhere until DPR
  // restores it. It has no kiddie section, so it loads via
  // `hasWaterslide` (see isVisible in src/store.ts). Empty `seasons` per the
  // closedForSeason convention; restore the indoor schedule when it reopens.
  {
    poolId: "barry-farm-indoor",
    name: "Barry Farm Aquatic Center",
    address: "1230 Sumner Road SE, Washington, DC 20020",
    ward: 8,
    lat: 38.8539,
    lng: -76.9955,
    hasKiddiePool: false,
    indoor: true,
    hasWaterslide: true,
    closedForSeason: true,
    seasons: [],
  },
  {
    poolId: "jelleff",
    name: "Jelleff Pool",
    address: "3265 S Street NW, Washington, DC 20007",
    ward: 2,
    lat: 38.912,
    lng: -77.067,
    hasKiddiePool: false,
    indoor: false,
    closedForSeason: true,
    seasons: [],
  },
  {
    poolId: "randall",
    name: "Randall Pool",
    address: "25 I Street SW, Washington, DC 20024",
    ward: 6,
    lat: 38.8775,
    lng: -77.013,
    hasKiddiePool: false,
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    indoor: false,
    closedForSeason: true,
    seasons: [],
  },
  {
    poolId: "kelly-miller",
    name: "Kelly Miller Pool",
    address: "4900 Brooks Street NE, Washington, DC 20019",
    ward: 7,
    lat: 38.8985,
    lng: -76.929,
    hasKiddiePool: false,
    indoor: false,
    closedForSeason: true,
    seasons: [],
  },
  {
    poolId: "rosedale",
    name: "Rosedale Pool",
    address: "1701 Gales Street NE, Washington, DC 20002",
    ward: 7,
    lat: 38.8965,
    lng: -76.979,
    hasKiddiePool: false,
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    indoor: false,
    closedForSeason: true,
    seasons: [],
  },
  {
    poolId: "douglass",
    name: "Douglass Pool",
    address: "1921 Frederick Douglass Court SE, Washington, DC 20020",
    ward: 8,
    lat: 38.861,
    lng: -76.987,
    hasKiddiePool: false,
    slopedEntry: true, // DPR zero-entry list: sloped entry + wading area.
    indoor: false,
    closedForSeason: true,
    seasons: [],
  },

  // ----- Splash pads (DPR spray parks) as crowd-reportable Pools -----
  // Appended so listPools()/SAMPLE_POOLS include them. They carry kind:"splash"
  // and report on their `main` section; the catalog invariant tests exclude
  // `kind === "splash"` from the swimming-pool structural checks.
  ...SPLASH_POOLS,
];

/**
 * Pools closed for renovation in 2026 and fully HIDDEN from the app — excluded
 * from SEED_POOLS and never seeded. (Pools that should instead be SHOWN as
 * "closed for the season" live in SEED_POOLS with `closedForSeason: true`.)
 * Identity preserved here so they are easy to re-activate next season: move the
 * entry into SEED_POOLS and add its SeasonWindows.
 */
export const CLOSED_FOR_RENOVATION_2026: Pool[] = [
  {
    poolId: "east-potomac",
    name: "East Potomac Pool",
    address: "972 Ohio Drive SW, Washington, DC 20024",
    ward: 6,
    lat: 38.869,
    lng: -77.027,
    hasKiddiePool: false,
    indoor: false,
    seasons: [],
  },
];
